import { useCallback, useEffect, useRef, useState } from 'react'

const CURRENT_VERSION = process.env.REACT_APP_VERSION
const POLL_INTERVAL_MS = 60 * 1000 // 1 minute

const fetchServerVersion = async () => {
  const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' })
  if (!res.ok) return null
  const data = await res.json()
  return data?.version || null
}

/**
 * Detects when a newer build has been deployed by comparing the version baked
 * into this bundle against the version served in /version.json.
 *
 * No-op for local/dev builds where REACT_APP_VERSION is empty or "dev".
 */
export default function useAppVersion() {
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const intervalRef = useRef(null)

  useEffect(() => {
    const versioningDisabled = !CURRENT_VERSION || CURRENT_VERSION === 'dev'
    if (versioningDisabled) return undefined

    let stopped = false

    const stopPolling = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    const check = async () => {
      if (stopped) return
      try {
        const serverVersion = await fetchServerVersion()
        if (serverVersion && serverVersion !== CURRENT_VERSION) {
          stopped = true
          stopPolling()
          setUpdateAvailable(true)
        }
      } catch {
        // network hiccup - ignore and retry on next tick
      }
    }

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') check()
    }

    check()
    intervalRef.current = setInterval(check, POLL_INTERVAL_MS)
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      stopped = true
      stopPolling()
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [])

  // Stable reference so effects depending on it run only when updateAvailable changes
  const reload = useCallback(() => window.location.reload(), [])

  return { updateAvailable, reload }
}
